import React, { useEffect, useRef } from 'react';
import { EXTRACT_CONFIGS } from '../../game/data/extractions';
import { publicAssetUrl } from '../../lib/publicAssetUrl';

/**
 * @param {object|null} activeItem — { type, strainName, gramsInput, gramsOutput, status }
 * @param {number} tick — game tick (used for tick-based progress if progressOverride omitted)
 * @param {number|null|undefined} progressOverride — 0..1 when progress is not tick-based (e.g. processing room jobs)
 * @param {string} [idleSubtext] — optional idle line under the title
 */
export function LabViewport({ activeItem, tick, progressOverride, idleSubtext }) {
  const videoRef = useRef(null);
  const cfg = activeItem && EXTRACT_CONFIGS[activeItem.type] ? EXTRACT_CONFIGS[activeItem.type] : null;

  const tickProgress =
    activeItem?.status === 'processing' && activeItem.startTick != null && activeItem.durationTicks > 0
      ? Math.min(1, (tick - activeItem.startTick) / activeItem.durationTicks)
      : 0;
  const progress =
    progressOverride != null && Number.isFinite(progressOverride)
      ? Math.min(1, Math.max(0, progressOverride))
      : tickProgress;

  const pct = Math.round(progress * 100);
  const videoSrc = cfg ? publicAssetUrl(`assets/${cfg.videoFile}`) : null;

  const isActive = !!activeItem && !!cfg;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !videoSrc) return;
    el.muted = true;
    el.setAttribute('playsInline', '');
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch((err) => console.warn('[LabViewport] play() blocked or failed', videoSrc, err?.message || err));
    }
  }, [videoSrc, isActive]);

  return (
    <div
      className="lab-viewport"
      style={isActive ? {
        borderColor: cfg.borderColor,
        boxShadow: `inset 0 0 20px rgba(0,0,0,0.6), 0 0 18px ${cfg.glowColor}, 0 0 0 1px rgba(0,0,0,0.4)`,
      } : {}}
    >
      <div className="lab-rivet lab-rivet-tl" />
      <div className="lab-rivet lab-rivet-tr" />
      <div className="lab-rivet lab-rivet-bl" />
      <div className="lab-rivet lab-rivet-br" />
      <div className="lab-glass-reflection" />
      <div className="lab-frame-label">
        {isActive
          ? (activeItem.queuePhase === 'pending'
            ? `⚗ ${cfg.label} — queued`
            : `⚗ ${cfg.label} Bay — active`)
          : '⚗ Processing Bay'}
      </div>

      {isActive ? (
        <>
          <video
            ref={videoRef}
            key={videoSrc}
            className="lab-video"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            src={videoSrc}
            onError={() => {
              console.warn('[LabViewport] failed to load or decode', videoSrc);
            }}
          />
          <div className="lab-progress-overlay">
            <div className="lab-progress-label">
              {cfg.icon} {activeItem.strainName} — {cfg.label}{' '}
              <span style={{ opacity: 0.7 }}>({activeItem.gramsInput}g → ~{activeItem.gramsOutput}g)</span>
            </div>
            <div className="lab-progress-track">
              <div
                className="lab-progress-fill"
                style={{
                  width: `${pct}%`,
                  background: `linear-gradient(90deg, ${cfg.borderColor}, ${cfg.glowColor.replace('0.3', '0.9')})`,
                  boxShadow: `0 0 8px ${cfg.glowColor}`,
                }}
              />
            </div>
            <div className="lab-progress-pct">{pct}%</div>
          </div>
        </>
      ) : (
        <div className="lab-idle-state">
          <div className="lab-idle-icon">⚗️</div>
          <div className="lab-idle-text">Processing Bay Idle</div>
          <div className="lab-idle-subtext">{idleSubtext || 'Unlock research (E1/E2), install equipment, then queue an extract.'}</div>
        </div>
      )}
    </div>
  );
}
